import {
    type FC,
    Fragment,
    useContext,
    useMemo,
} from "react";
import { Toggle } from "@onshopfront/components";
import {
    caseText,
    pluralize,
    singleText,
    uppercaseFirst,
} from "../../../../Utilities/Generic/Strings";
import { DragDropEditorContext, type DragDropSettingsProps } from "../../../Forms/DragDrop/DragDropTypes";
import { type ColumnType, ReorderableTableSettings } from "../../../Forms/DragDrop/ReorderableTableSettings";
import { ReceiptTypeContext } from "../../Receipt";
import { isReceiptProductTableValue } from "../ReceiptProductTableComponent";

export const ReceiptProductTableSettings: FC<DragDropSettingsProps> = ({
    id,
    value,
}) => {
    const type = useContext(ReceiptTypeContext);

    const { manager } = useContext(DragDropEditorContext);

    // Defined during component initialization to handle setting changes
    const columnDefinitions = useMemo((): Record<string, string> => {
        if(type === "TEXT_ONLY") {
            return {
                qty    : "QTY",
                product: "Product",
                price  : "Total",
            };
        }

        return {
            "qty"         : "QTY",
            "product"     : "Product",
            "case-qty"    : "Case QTY",
            "item-price"  : `${uppercaseFirst(singleText())} Price`,
            "case-price"  : `${uppercaseFirst(caseText())} Price`,
            "discount"    : "Discount",
            "price"       : "Total",
            "normal-price": "Normal Price",
            "total-items" : `Total ${uppercaseFirst(pluralize(singleText(), 2))}`,
            "savings"     : "Savings",
        };
    }, [ type ]);

    let columns: Array<ColumnType> = [];
    let showDiscountReason = false;
    let showProductNote = false;

    if(isReceiptProductTableValue(value)) {
        columns = value.columns;
        showDiscountReason = value.showDiscountReason;
        showProductNote = value.showProductNote;
    }

    return (
        <Fragment>
            <ReorderableTableSettings
                value={columns}
                onChange={columns => {
                    manager.updateComponent(id, {
                        ...(isReceiptProductTableValue(value) ? value : {}),
                        columns,
                    });
                }}
                renameable
                columns={columnDefinitions}
            />
            <label className="flex items-center gap-2">
                <Toggle
                    name="showDiscountReason"
                    checked={showDiscountReason || false}
                    onChange={e => {
                        manager.updateComponent(id, {
                            ...(isReceiptProductTableValue(value) ? value : {}),
                            showDiscountReason: e.currentTarget.checked,
                        });
                    }}
                />
                Show discount reason
            </label>
            <label className="flex items-center gap-2">
                <Toggle
                    name="showProductNote"
                    checked={showProductNote || false}
                    onChange={e => {
                        manager.updateComponent(id, {
                            ...(isReceiptProductTableValue(value) ? value : {}),
                            showProductNote: e.currentTarget.checked,
                        });
                    }}
                />
                Show product note
            </label>
        </Fragment>
    );
};
